import { submitFormJson } from 'widgets/toolbox/ajax';
import { TLabelInstance } from 'widgets/global/Label';
import { TButtonWithIconInstance } from 'widgets/global/ButtonWithIcon';

type TWishlistItem = {
    id: string;
    optionId: string;
    selectedValueId: string;
};

type TResponse = TServerResponse & {
    itemAlreadyExists: boolean;
    msg: string;
    pid: string;
    wishlistItems: Array<TWishlistItem>;
};

declare const window: ExtendedWindow;

/**
 * @param Widget Base widget for extending
 * @returns Add to Wishlist Mixin class
 */
function AddToWishlistMixinExtClassCreator(AddToWishlistMixin: import('wishlist/wishlist/AddToWishlistMixin').TAddToWishlistMixin) {
    /**
     * @category widgets
     * @subcategory plugin_wishlist
     * @class AddToWishlistMixin
     * @augments Widget
     * @classdesc Add to wishlist mixin with next features:
     * 1. Represents `Add to wishlist` button together with related functionality
     * <br>*As far as it's a mixin, separate DOM widget is not needed.*
     * 2. Allows to add products to wishlist from PDP, QuickView
     * 3. Communicates to user operation results in both cases: error and success
     *
     * Widget has next relationship:
     * * Listens click on `add button` that instance of {@link Button} to handle addition to wish list.
     * <br>Also uses component methods to change button text, activate/deactivate button
     * * Uses methods of `add message` that instance of {@link Label} to set/hide/show messsage
     * @property {string} data-pid - product id
     * @property {string} [data-text-network-error=Network Error] - Network error message
     * @property {string} [data-add-to-wishlist-msg=addToWishlistMsg] - ID of {@link Label} component that represents `add msg`
     * @property {string} [data-add-to-wishlist-btn=addToWishlist] -  ID of {@link Button} component that represents `add button`
     * @property {string} data-text-added-to-wishlist - Added to wishlist message
     * @property {boolean} [add-to-wishlist-hide-texts=false] - Added to wishlist message
     * @property {string} data-original-wishlist-button-message - original message for `add to wishlist` button
     * @property {boolean} data-show-alert-if-product-in-wishlist - Indicates, if we need to show active icon for wishlist button,
     * in case if request returns, that item already in wishlist
     * @property {string} [data-class-added-to-wishlist] - Added to wishlist class
     */
    class AddToWishlistMixinExt extends AddToWishlistMixin {
        /**
         * @description Show error/success message
         * @param addButton - Target button
         * @listens dom#click
         */
        addToWishlist(addButton: TButtonWithIconInstance) {
            if (!this.prefs().allowVgAdding && !this.callIfExists('checkUnselectedAttributes')) {
                return null;
            }

            const addToWishlistUrl = window.sfccData.wishlistActionUrls?.addToWishlist;
            const options = this.prefs().productOptions;
            let customOptionValue = '';
            let optionId = '';
            let optionVal = '';

            if (options?.length) {
                optionId = options[0].id;
                optionVal = options[0].selectedValueId;

                if (this.mixedToProductDetails()) {
                    if (this.isCustomOptionValid(options[0].id)) {
                        customOptionValue = <string>this.getCustomOptionValue(options[0].id);
                    } else {
                        return null;
                    }
                }
            }

            addButton.busy();
            this.getById(this.prefs().addToWishlistMsg, (labelAddToWishlistMsg: TLabelInstance) => labelAddToWishlistMsg.hide());

            return submitFormJson(addToWishlistUrl, {
                pid: typeof this.config.variationGroupId === 'string' ? this.config.variationGroupId : this.prefs().pid,
                qty: this.prefs().selectedQuantity,
                optionId: optionId,
                optionVal: optionVal,
                customOptionValue: customOptionValue
            })
                .then((response: TResponse) => {
                    this.handleSuccess(response);

                    this.eventBus().emit('product.added.to.wishlist', this);
                    this.eventBus().emit('wishlist.rerender', response);

                    return response;
                })
                .catch(() => {
                    /**
                     * @description Global event to show alert
                     * @event "alert.error"
                     */
                    // eslint-disable-next-line sonarjs/no-duplicate-string
                    this.eventBus().emit('alert.error', {
                        accessibilityAlert: this.prefs().textNetworkError
                    });
                })
                .finally(() => {
                    addButton.unbusy();
                });
        }
    }

    return AddToWishlistMixinExt;
}

export type TAddToWishlistMixinExt = ReturnType<typeof AddToWishlistMixinExtClassCreator>;

export type TAddToWishlistMixinExtInstance = InstanceType<TAddToWishlistMixinExt>;

export default AddToWishlistMixinExtClassCreator;
